import React, { useState, useEffect } from 'react';
import {
    Plus, Trash2, Edit, Save, X, Image as ImageIcon, Upload,
    LayoutDashboard, Package, Settings, LogOut, Menu, User,
    Search, BarChart3
} from 'lucide-react';
import { db, auth } from '../../firebase';
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import './AdminPanel.css';

// Configuración de Cloudinary - Reemplaza con tus credenciales
const CLOUDINARY_CLOUD_NAME = 'dr4u5inmi';
const CLOUDINARY_UPLOAD_PRESET = 'nutriactiva_preset'; // Paso final: Crea esto en tu consola de Cloudinary

const initialSupplements = [
    { nombre: 'Isolate Protein', precio: 189.90, categoria: 'Proteínas', subcategoria: 'Isolada', marca: 'Optimum Nutrition', stock: 15, sku: 'ON-ISO-01', descripcion: 'Proteína aislada de alta calidad.', imagen: 'https://res.cloudinary.com/dq7vv8dfu/image/upload/v1710123456/isolada.jpg' },
    { nombre: 'Creatina Monohidratada', precio: 100.00, categoria: 'Creatina', subcategoria: 'Pura', marca: 'MuscleTech', stock: 20, sku: 'MT-CRE-01', descripcion: 'Creatina para mejorar fuerza.', imagen: 'https://res.cloudinary.com/dq7vv8dfu/image/upload/v1710123457/creatina.jpg' }
];

const initialCategories = [{ nombre: 'Proteínas' }, { nombre: 'Creatina' }, { nombre: 'Aminoácidos' }, { nombre: 'Pre-entreno' }];
const initialSubcategories = [
    { nombre: 'Isolada', categoriaPadre: 'Proteínas' },
    { nombre: 'Concentrada', categoriaPadre: 'Proteínas' },
    { nombre: 'Pura', categoriaPadre: 'Creatina' },
    { nombre: 'Micronizada', categoriaPadre: 'Creatina' }
];
const initialBrands = [{ nombre: 'Optimum Nutrition' }, { nombre: 'MuscleTech' }, { nombre: 'Dymatize' }, { nombre: 'Universal' }];

const AdminPanel = () => {
    const navigate = useNavigate();
    const [activeModule, setActiveModule] = useState('inventory'); // dashboard, inventory, settings
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [supplements, setSupplements] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // Listas para selects y gestión
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [brands, setBrands] = useState([]);

    const [currentProduct, setCurrentProduct] = useState({
        id: null,
        nombre: '',
        descripcion: '',
        precio: '',
        imagen: '',
        categoria: '',
        subcategoria: '',
        marca: '',
        stock: 0,
        sku: ''
    });

    // Gestión de Configuración
    const [configTab, setConfigTab] = useState('categorias'); // categorias, subcategorias, marcas
    const [newItemName, setNewItemName] = useState('');
    const [tempParentCategory, setTempParentCategory] = useState('');

    // Búsqueda y Filtros de Inventario
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState('Todas');

    useEffect(() => {
        // Escuchar productos
        const unsubProducts = onSnapshot(collection(db, 'suplementos'), (snapshot) => {
            const productsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setSupplements(productsData);

            // Si el inventario está vacío, pedir cargar datos iniciales
            if (productsData.length === 0 && snapshot.metadata.fromCache === false) {
                if (window.confirm('¿Deseas cargar los datos iniciales y colecciones en Firebase?')) {
                    uploadInitialData();
                }
            }
        });

        // Escuchar categorías (ordenar alfabéticamente)
        const unsubCats = onSnapshot(collection(db, 'categorias'), (snapshot) => {
            setCategories(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a, b) => a.nombre.localeCompare(b.nombre)));
        });

        // Escuchar subcategorías
        const unsubSubcats = onSnapshot(collection(db, 'subcategorias'), (snapshot) => {
            setSubcategories(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a, b) => a.nombre.localeCompare(b.nombre)));
        });

        // Escuchar marcas
        const unsubBrands = onSnapshot(collection(db, 'marcas'), (snapshot) => {
            setBrands(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a, b) => a.nombre.localeCompare(b.nombre)));
        });

        return () => {
            unsubProducts();
            unsubCats();
            unsubSubcats();
            unsubBrands();
        };
    }, []);

    const uploadInitialData = async () => {
        setIsLoading(true);
        try {
            // Cargar Categorías
            for (const item of initialCategories) {
                await addDoc(collection(db, 'categorias'), item);
            }
            // Cargar Subcategorías
            for (const item of initialSubcategories) {
                await addDoc(collection(db, 'subcategorias'), item);
            }
            // Cargar Marcas
            for (const item of initialBrands) {
                await addDoc(collection(db, 'marcas'), item);
            }
            // Cargar Suplementos
            for (const item of initialSupplements) {
                await addDoc(collection(db, 'suplementos'), item);
            }
            alert('Colecciones e información inicial cargadas con éxito');
        } catch (error) {
            console.error("Error al cargar datos:", error);
            alert('Error al migrar datos');
        }
        setIsLoading(false);
    };

    const handleLogout = async () => {
        try {
            await signOut(auth);
            navigate('/admin/login');
        } catch (error) { console.error("Error logout:", error); }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        // Si cambia la categoría, resetear la subcategoría
        if (name === 'categoria') {
            setCurrentProduct(prev => ({ ...prev, [name]: value, subcategoria: '' }));
        } else {
            setCurrentProduct(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleImageChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            setIsLoading(true);
            try {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
                const response = await fetch(`https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`, {
                    method: 'POST', body: formData,
                });
                if (!response.ok) throw new Error('Error Cloudinary');
                const data = await response.json();
                setCurrentProduct(prev => ({ ...prev, imagen: data.secure_url }));
            } catch (error) { console.error("Error image:", error); }
            setIsLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            const productData = {
                nombre: currentProduct.nombre,
                descripcion: currentProduct.descripcion,
                precio: Number(currentProduct.precio),
                imagen: currentProduct.imagen,
                categoria: currentProduct.categoria,
                subcategoria: currentProduct.subcategoria,
                marca: currentProduct.marca,
                stock: Number(currentProduct.stock),
                sku: currentProduct.sku
            };

            if (currentProduct.id) {
                await updateDoc(doc(db, 'suplementos', currentProduct.id), productData);
            } else {
                await addDoc(collection(db, 'suplementos'), productData);
            }
            resetForm();
        } catch (error) { console.error("Error submit:", error); }
        setIsLoading(false);
    };

    const resetForm = () => {
        setCurrentProduct({
            id: null, nombre: '', descripcion: '', precio: '', imagen: '',
            categoria: '', subcategoria: '', marca: '', stock: 0, sku: ''
        });
        setIsEditing(false);
    };

    const handleEdit = (product) => {
        setCurrentProduct(product);
        setIsEditing(true);
        document.querySelector('.form-section')?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleDelete = async (id) => {
        if (window.confirm('¿Eliminar producto?')) {
            setIsLoading(true);
            try { await deleteDoc(doc(db, 'suplementos', id)); } catch (error) { console.error(error); }
            setIsLoading(false);
        }
    };

    // Funciones de Configuración
    const handleAddConfigItem = async (e) => {
        e.preventDefault();
        if (!newItemName.trim()) return;

        const data = { nombre: newItemName };
        if (configTab === 'subcategorias') {
            if (!tempParentCategory) return alert('Selecciona una categoría padre');
            data.categoriaPadre = tempParentCategory;
        }

        try {
            await addDoc(collection(db, configTab), data);
            setNewItemName('');
            setTempParentCategory(''); // Reset parent category after adding
        } catch (error) { console.error(error); }
    };

    const handleDeleteConfigItem = async (id) => {
        if (window.confirm('¿Eliminar este elemento?')) {
            try { await deleteDoc(doc(db, configTab, id)); } catch (error) { console.error(error); }
        }
    };

    const renderInventoryModule = () => {
        // Filtrar subcategorías según la categoría seleccionada
        const filteredSubcategories = subcategories.filter(s => s.categoriaPadre === currentProduct.categoria);

        return (
            <div className="module-content fade-in">
                <header className="module-header">
                    <div>
                        <h1>Gestión de Productos</h1>
                        <p>Administra tu catálogo con información detallada</p>
                    </div>
                </header>

                <div className="inventory-container">
                    <section className="form-section glass-card">
                        <div className="section-header">
                            <h2>{isEditing ? 'Editar Producto' : 'Nuevo Producto'}</h2>
                            {isEditing && <button className="icon-btn close" onClick={resetForm}><X size={18} /></button>}
                        </div>

                        <form onSubmit={handleSubmit} className="admin-form">
                            <div className="form-group">
                                <label>Nombre del Producto</label>
                                <input type="text" name="nombre" value={currentProduct.nombre} onChange={handleInputChange} required />
                            </div>

                            <div className="form-row three-cols">
                                <div className="form-group">
                                    <label>Precio (S/.)</label>
                                    <input type="number" step="0.01" name="precio" value={currentProduct.precio} onChange={handleInputChange} required />
                                </div>
                                <div className="form-group">
                                    <label>Stock</label>
                                    <input type="number" name="stock" value={currentProduct.stock} onChange={handleInputChange} required />
                                </div>
                                <div className="form-group">
                                    <label>SKU</label>
                                    <input type="text" name="sku" value={currentProduct.sku} onChange={handleInputChange} placeholder="EJ: WHEY-01" />
                                </div>
                            </div>

                            <div className="form-row three-cols">
                                <div className="form-group">
                                    <label>Categoría</label>
                                    <select name="categoria" value={currentProduct.categoria} onChange={handleInputChange} required>
                                        <option value="">Seleccionar...</option>
                                        {categories.map(c => <option key={c.id} value={c.nombre}>{c.nombre}</option>)}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Subcategoría</label>
                                    <select name="subcategoria" value={currentProduct.subcategoria} onChange={handleInputChange} disabled={!currentProduct.categoria}>
                                        <option value="">{currentProduct.categoria ? 'Ninguna' : 'Elige categoría primero'}</option>
                                        {filteredSubcategories.map(s => <option key={s.id} value={s.nombre}>{s.nombre}</option>)}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Marca</label>
                                    <select name="marca" value={currentProduct.marca} onChange={handleInputChange} required>
                                        <option value="">Seleccionar...</option>
                                        {brands.map(b => <option key={b.id} value={b.nombre}>{b.nombre}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Imagen</label>
                                <div className="image-upload-wrapper">
                                    <input type="file" accept="image/*" onChange={handleImageChange} id="img-upload" hidden />
                                    <label htmlFor="img-upload" className="upload-area">
                                        {currentProduct.imagen ? (
                                            <div className="preview-container">
                                                <img src={currentProduct.imagen} alt="Preview" />
                                                <div className="overlay"><Upload size={24} /></div>
                                            </div>
                                        ) : (
                                            <div className="upload-placeholder">
                                                <Upload size={32} />
                                                <span>Subir Imagen</span>
                                            </div>
                                        )}
                                    </label>
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Descripción</label>
                                <textarea name="descripcion" value={currentProduct.descripcion} onChange={handleInputChange} required rows="4" />
                            </div>

                            <button type="submit" className="primary-btn">
                                {isEditing ? <Save size={18} /> : <Plus size={18} />}
                                {isEditing ? 'Guardar Cambios' : 'Registrar Producto'}
                            </button>
                        </form>
                    </section>

                    <section className="list-section">
                        <div className="list-controls glass-card">
                            <div className="search-box">
                                <Search size={18} />
                                <input
                                    type="text"
                                    placeholder="Buscar por nombre o SKU..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <div className="filter-box">
                                <label>Categoría:</label>
                                <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
                                    <option value="Todas">Todas</option>
                                    {categories.map(c => <option key={c.id} value={c.nombre}>{c.nombre}</option>)}
                                </select>
                            </div>
                        </div>

                        <div className="inventory-stats glass-card">
                            <div className="stat"><span>Total</span><strong>{supplements.length}</strong></div>
                            <div className="stat"><span>Bajo Stock</span><strong className="text-red">{supplements.filter(p => p.stock < 5).length}</strong></div>
                        </div>

                        <div className="inventory-grid">
                            {supplements
                                .filter(product => {
                                    const matchesSearch = product.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                        product.sku?.toLowerCase().includes(searchTerm.toLowerCase());
                                    const matchesCategory = filterCategory === 'Todas' || product.categoria === filterCategory;
                                    return matchesSearch && matchesCategory;
                                })
                                .map(product => (
                                    <div key={product.id} className="inventory-card glass-card">
                                        <div className="card-image">
                                            {product.imagen ? <img src={product.imagen} alt={product.nombre} /> : <ImageIcon size={32} />}
                                            <div className="card-stock-badge">{product.stock} u.</div>
                                        </div>
                                        <div className="card-info">
                                            <div className="tags-wrapper">
                                                <span className="category-tag">{product.categoria}</span>
                                                {product.marca && <span className="brand-tag">{product.marca}</span>}
                                            </div>
                                            <h3>{product.nombre}</h3>
                                            <div className="card-meta">
                                                <p className="sku">SKU: {product.sku || 'N/A'}</p>
                                                <p className="price">S/. {product.precio}</p>
                                            </div>
                                            <div className="card-actions">
                                                <button onClick={() => handleEdit(product)} className="icon-btn edit"><Edit size={16} /></button>
                                                <button onClick={() => handleDelete(product.id)} className="icon-btn delete"><Trash2 size={16} /></button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                        </div>
                    </section>
                </div>
            </div>
        );
    };

    const renderDashboardModule = () => (
        <div className="module-content fade-in">
            <header className="module-header">
                <div><h1>Resumen General</h1><p>Estado actual de NutriMayra</p></div>
            </header>
            <div className="dashboard-grid">
                <div className="stat-card glass-card">
                    <div className="icon-box blue"><Package size={24} /></div>
                    <div className="details"><span>Productos</span><h3>{supplements.length}</h3></div>
                    <BarChart3 size={20} className="chart-icon" />
                </div>
                <div className="stat-card glass-card">
                    <div className="icon-box green"><BarChart3 size={24} /></div>
                    <div className="details"><span>Stock Total</span><h3>{supplements.reduce((acc, p) => acc + Number(p.stock || 0), 0)}</h3></div>
                </div>
            </div>
        </div>
    );

    const renderSettingsModule = () => (
        <div className="module-content fade-in">
            <header className="module-header">
                <div><h1>Configuración</h1><p>Gestiona los atributos del inventario</p></div>
            </header>

            <div className="settings-container glass-card">
                <div className="settings-tabs">
                    <button className={`tab-btn ${configTab === 'categorias' ? 'active' : ''}`} onClick={() => setConfigTab('categorias')}>Categorías</button>
                    <button className={`tab-btn ${configTab === 'subcategorias' ? 'active' : ''}`} onClick={() => setConfigTab('subcategorias')}>Subcategorías</button>
                    <button className={`tab-btn ${configTab === 'marcas' ? 'active' : ''}`} onClick={() => setConfigTab('marcas')}>Marcas</button>
                </div>

                <div className="settings-panel-content">
                    <form onSubmit={handleAddConfigItem} className="config-add-form flex-col">
                        <div className="form-row">
                            <input
                                type="text"
                                placeholder={`Nombre de la ${configTab.slice(0, -1)}...`}
                                value={newItemName}
                                onChange={(e) => setNewItemName(e.target.value)}
                                required
                            />
                            {configTab === 'subcategorias' && (
                                <select
                                    value={tempParentCategory}
                                    onChange={(e) => setTempParentCategory(e.target.value)}
                                    required
                                    className="settings-parent-select"
                                >
                                    <option value="">Categoría Padre...</option>
                                    {categories.map(c => <option key={c.id} value={c.nombre}>{c.nombre}</option>)}
                                </select>
                            )}
                            <button type="submit" className="add-btn"><Plus size={18} /> Añadir</button>
                        </div>
                    </form>

                    <div className="config-items-list">
                        {(configTab === 'categorias' ? categories :
                            configTab === 'subcategorias' ? subcategories :
                                brands).map(item => (
                                    <div key={item.id} className="config-item">
                                        <div className="item-main-info">
                                            <strong>{item.nombre}</strong>
                                            {item.categoriaPadre && <span className="parent-badge">{item.categoriaPadre}</span>}
                                        </div>
                                        <button onClick={() => handleDeleteConfigItem(item.id)} className="delete-icon-btn"><Trash2 size={16} /></button>
                                    </div>
                                ))}
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className={`admin-layout ${isSidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
            <aside className="sidebar">
                <div className="sidebar-brand"><div className="logo-icon">NM</div><span>NutriMayra Admin</span></div>
                <nav className="sidebar-nav">
                    <button className={`nav-item ${activeModule === 'dashboard' ? 'active' : ''}`} onClick={() => setActiveModule('dashboard')}><LayoutDashboard size={20} /><span>Dashboard</span></button>
                    <button className={`nav-item ${activeModule === 'inventory' ? 'active' : ''}`} onClick={() => setActiveModule('inventory')}><Package size={20} /><span>Inventario</span></button>
                    <button className={`nav-item ${activeModule === 'settings' ? 'active' : ''}`} onClick={() => setActiveModule('settings')}><Settings size={20} /><span>Configuración</span></button>
                </nav>
                <div className="sidebar-footer">
                    <button className="logout-btn-alt" onClick={handleLogout}><LogOut size={20} /><span>Cerrar Sesión</span></button>
                </div>
            </aside>
            <main className="main-viewport">
                <header className="top-bar">
                    <button className="menu-toggle" onClick={() => setIsSidebarOpen(!isSidebarOpen)}><Menu size={24} /></button>
                    <div className="user-profile"><div className="avatar"><User size={20} /></div><span>Mayra C.</span></div>
                </header>
                <div className="module-viewport">
                    {activeModule === 'dashboard' && renderDashboardModule()}
                    {activeModule === 'inventory' && renderInventoryModule()}
                    {activeModule === 'settings' && renderSettingsModule()}
                </div>
            </main>
            {isLoading && <div className="loading-overlay"><div className="spinner" /></div>}
        </div>
    );
};

export default AdminPanel;